import { useState, useRef } from "react";
import { Loader2, Flag } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/i18n/I18nContext";
import { api, isConnectionError } from "@/lib/api";
import { chatErrorCode, chatErrorMessage } from "@/lib/conversationWrite";

const REPORT_REASONS = ["spam", "harassment", "inappropriate", "fake", "other"];

// Controlled report dialog for reporting a member OR a specific message.
// Only sends the fields the backend contract accepts; no internal moderation data.
export function ReportDialog({ open, onOpenChange, reportedMemberId, reportedName, conversationId, messageId, postId, commentId, title, description }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const flight = useRef(false);
  const isMessage = !!messageId;

  const close = (v) => {
    if (!v) { setReason(""); setNote(""); }
    onOpenChange(v);
  };

  const submit = async () => {
    if (!reason || submitting || flight.current) return;
    flight.current = true;
    setSubmitting(true);
    try {
      const payload = { reported_member_id: reportedMemberId, reason };
      if (note.trim()) payload.note = note.trim();
      if (conversationId) payload.conversation_id = conversationId;
      if (messageId) payload.message_id = messageId;
      if (postId) payload.post_id = postId;
      if (commentId) payload.comment_id = commentId;
      await api.post("/member/reports", payload);
      toast.success(t("member.p2b.report_success"));
      close(false);
    } catch (err) {
      toast.error(chatErrorMessage(chatErrorCode(err, { ctx: "report", connection: isConnectionError(err) }), t));
    } finally {
      flight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="member-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-brand-magenta" aria-hidden="true" />
            {title || (isMessage ? t("member.p2b.report_message_title") : t("member.p2b.report_member_title"))}
          </DialogTitle>
          <DialogDescription>
            {description || (isMessage
              ? t("member.p2b.report_message_desc")
              : t("member.p2b.report_member_desc", { name: reportedName || "" }))}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="report-reason" className="text-sm font-medium text-brand-purple">
              {t("member.p2b.report_reason")}
            </label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="report-reason" data-testid="member-report-reason" aria-label={t("member.p2b.report_reason")}>
                <SelectValue placeholder={t("member.p2b.report_reason_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`member-report-reason-${r}`}>
                    {t(`member.p2b.reason_${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="report-note" className="text-sm font-medium text-brand-purple">
              {t("member.p2b.report_note")}
            </label>
            <Textarea id="report-note" value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="member-report-note" rows={3} placeholder={t("member.p2b.report_note_placeholder")} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="member-report-cancel"
            className="border-brand-purple/20 text-brand-purple">
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={!reason || submitting} data-testid="member-report-submit"
            className="gap-1.5 bg-brand-magenta hover:bg-brand-magenta-dark">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />}
            {t("member.p2b.report_submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
